import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import micIcon from "../../../../assets/icons/mic-icon.svg";
import { routes } from "../../../../constants/routes";
import { getNotes, noteCreateOrUpdate } from "../../../../api/notes";
import "./general-notes-widget.scss";
import VoiceNoteDialog from "../../../surveys/notes-list/voice-note-dialog";
import { convertUTCToLocalRelative } from "../../../../utils/utils";
import MODULES from "../../../../constants/modules";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import { AlertType } from "../../../../utils/util-types";
import { ALERT_TYPE } from "../../../../utils/constants";
import store from "../../../../store";

interface NotesSectionProps {
  setIsLoading: (isLoading: boolean) => void;
  setAlertOpen: (isOpen: boolean) => void;
  setAlertMessage: (message: string) => void;
  setAlertType: (type: AlertType) => void;
}

const NotesSection: React.FC<NotesSectionProps> = ({
  setIsLoading,
  setAlertOpen,
  setAlertMessage,
  setAlertType,
}) => {
  const [notes, setNotesData] = useState<any[]>([]);
  const [showVoiceNoteDialog, setShowVoiceNoteDialog] = useState(false);
  const navigate = useNavigate();
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const { canWrite, canRead } = useResourcePermissions(MODULES.GENERAL_NOTES);

  const [editMode, setEditMode] = useState(
    store.getState().dashboard.editMode,
  );
  const rolePermissions = store.getState().session.userDetails?.roles?.rolePermissions;
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      setEditMode(store.getState().dashboard.editMode);
    });

    return () => unsubscribe();
  }, []);

  const toggleExpand = (index: number) => {
    setExpandedNotes((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }

      return newSet;
    });
  };

  const handleViewAll = () => {
    if (!canRead) return;
    navigate(routes.GENERAL_NOTES);
  };
  const fetchNotesList = async () => {
    setIsLoading(true);
    try {
      const params: any = {
        limit: 10,
        offset: 0,
      };
      const response = await getNotes(params);
      setNotesData(response.data.data.data);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to fetch notes.",
      );
      setAlertType(ALERT_TYPE.ERROR);
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const addNoteHandler = async (noteContent: string) => {
    setIsLoading(true);
    try {
      await noteCreateOrUpdate({ note: noteContent, noteType: "TRANSCRIBED" });
      setAlertMessage("Note added successfully.");
      setAlertType(ALERT_TYPE.SUCCESS);
      setAlertOpen(true);
      fetchNotesList();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to add note.",
      );
      setAlertType(ALERT_TYPE.ERROR);
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (canRead) {
      fetchNotesList();
    }
  }, [canRead]);

  return (
    <div className="dashboard-chart-section-cards notes-widget">
      <div className="left-section">
        <div className="notes-list">
          {!canRead ? (
            <div className="no-notes">No read access to this module.</div>
          ) : notes.length > 0 ? (
            notes.map((note, index) => {
              const isExpanded = expandedNotes.has(index);
              return (
                <div key={index} className="note-item">
                  <div className={`note-text ${isExpanded ? "expanded" : ""}`}>
                    {note.notes}
                  </div>

                  {note.notes.length > 100 && (
                    <span
                      className="read-more"
                      onClick={() => toggleExpand(index)}
                    >
                      {isExpanded ? "Read less" : "Read more"}
                    </span>
                  )}
                  <div className="note-date">
                    {convertUTCToLocalRelative(note.created_at)}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-notes">No notes available.</div>
          )}
        </div>
      </div>
      <div className="note-right-section">
        <div
          className={`mic-icon  ${!canWrite || editMode ? "disabled" : ""}`}
          onClick={() => {
            if (canWrite && !editMode) setShowVoiceNoteDialog(true);
          }}
        >
          <img src={micIcon} alt="Microphone" height={30} />
        </div>
        <div className="tap-to-transcribe">Tap to transcribe a note</div>
        <div className="auto-text-note">
          Automatically create text notes from your voice inputs
        </div>
      </div>
      <VoiceNoteDialog
        open={showVoiceNoteDialog}
        onClose={() => setShowVoiceNoteDialog(false)}
        onSave={(text: string) => {
          setShowVoiceNoteDialog(false);
          addNoteHandler(text);
        }}
      />
    </div>
  );
};

export default NotesSection;
